import { AttachMoney } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { getDocumentReport } from 'lib/api/document';
import EnhancedTable from 'lib/designSystem/Table';
import { formatMoney } from 'lib/functions/utils';
import React from 'react';
import { useDispatch } from 'react-redux';

import InfoRow from './InfoRow';
import SectionSkeleton from './SectionSkeleton';

interface CollectionsSectionProps {
  documentId: string;
}

const CollectionsSection: React.FC<CollectionsSectionProps> = ({
  documentId,
}) => {
  const dispatch = useDispatch();
  const { data: document, isPending } = getDocumentReport(dispatch, documentId);

  if (!document || isPending) return <SectionSkeleton rowsPerColumn={4} />;
  const {
    arrearConciliation,
    documentConciliation,
    documentAmountDiscounted,
    conciliationLog,
  } = document;

  const getPaidOverdue =
    arrearConciliation !== undefined ? arrearConciliation : null;

  const getPaidPrincipal =
    documentConciliation !== undefined ? documentConciliation : null;

  const getPaidTotalDiscounted =
    documentAmountDiscounted !== undefined ? documentAmountDiscounted : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          backgroundColor: 'rgba(255, 152, 0, 0.08)',
          borderRadius: '8px',
          px: 1.5,
          py: 0.8,
          border: '1px solid rgba(255, 152, 0, 0.15)',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: 'warning.main',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            textAlign: 'center',
          }}
        >
          Recaudaciones
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 6, justifyContent: 'flex-start' }}>
        <InfoRow
          label="Abono Capital"
          value={getPaidPrincipal ? formatMoney(getPaidPrincipal) : null}
          maxLength={40}
          icon={
            <AttachMoney sx={{ fontSize: '0.8rem', color: 'warning.main' }} />
          }
        />
        <InfoRow
          label="Mora calculada"
          value={getPaidOverdue ? formatMoney(getPaidOverdue) : null}
          maxLength={40}
          icon={
            <AttachMoney sx={{ fontSize: '0.8rem', color: 'warning.main' }} />
          }
        />
        <InfoRow
          label="Monto Condonado"
          value={
            getPaidTotalDiscounted ? formatMoney(getPaidTotalDiscounted) : null
          }
          maxLength={40}
          icon={
            <AttachMoney sx={{ fontSize: '0.8rem', color: 'warning.main' }} />
          }
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <EnhancedTable
          data={conciliationLog ?? []}
          dataHeaders={[
            { label: 'ID operación', key: 'requestId' },
            { label: 'Tipo de operación', key: 'typeEsp' },
            { label: 'Pagado por', key: 'paidBy' },
            {
              label: 'Recaudación',
              key: 'conciliationDate',
              type: 'date',
              description:
                'Fecha en la que la recaudación fue registrada en Pruff',
            },
            {
              label: 'Pago recibido',
              key: 'payInDate',
              type: 'date',
              description:
                'Fecha en la que el pago fue recibido en la cuenta bancaria',
            },
            { label: 'Monto', key: 'amount', type: 'money' },
            {
              label: 'Capital',
              key: 'conciliationDocumentAmount',
              type: 'money',
            },
            {
              label: 'Mora',
              key: 'conciliationArrearAmount',
              type: 'money',
              description:
                'Monto mora (Se calcula con respecto a la fecha de "Pago recibido")',
            },
          ]}
          finalSumRow
        />
      </Box>
    </Box>
  );
};

export default CollectionsSection;
